import { useState } from 'react';
import { DockPanel, useDock } from '@hana/shell';
import { useT } from '@hana/i18n';
import { arSummary, type ArRow } from './ar';
import { RCP_ROWS } from './rcp';
import s from './ardock.module.css';
import './ardock.i18n';

// Bảng phải của Mint-AR (khổ 1920 mở sẵn, dock 372): Phân tích · Việc · Duyệt — theo code board Mint-AR `dkAn` / `dkTask` / `dkAppr`. TIỀN → sau quyền `money` (ẩn số khi không có). Chỉ đọc.
export type ArDockKey = 'an' | 'task' | 'appr';
export const useArDock = () => useDock<ArDockKey>('an');

const nf = (x: number, d = 1): string => x.toLocaleString('vi-VN', { minimumFractionDigits: d, maximumFractionDigits: d });
const AGC = ['var(--d-blue)', 'color-mix(in srgb, var(--d-amber) 55%, var(--card))', 'var(--d-amber)', 'var(--warn)', 'var(--err-bd)'];
const AGK = ['ad.ag0', 'ad.ag1', 'ad.ag2', 'ad.ag3', 'ad.ag4'] as const;
const MASK = '•••••';

const TASKS: readonly (readonly [string, 'tx' | 'err' | 'tx2', readonly (readonly [string, string, string, string])[]])[] = [
  ['ad.today', 'tx', [['DUN', 'Nhắc +7 · NAMMY · 310,0 tr', 'Hôm nay 17:00', 'MAITTN (SA)'], ['DUN', 'Nhắc mốc 0 · DSF2012 · DN26054355', 'Hôm nay 17:00', 'HOATTN (SA)'], ['DCF', 'RUITONGVN-SC chưa xác nhận DN26054420', 'Tự xác nhận 27/Oct', 'MAITTN (SA)']]],
  ['ad.late', 'err', [['DUN', 'Nhắc +15 · EmdeesFoods · 385,0 tr', 'Quá hạn 1 ngày', 'HOATTN (SA)'], ['DUN', 'Nhắc +30 · DAVICO · gọi kế toán', 'Quá hạn 3 ngày', 'HOATTN (SA)']]],
  ['ad.soon', 'tx2', [['DUN', 'Nhắc −3 · NKL · DN26054412', '16/Nov/2026', 'MAITTN (SA)']]],
];
const APPRS = [
  { k: 'Release HBL · Telex', job: 'SE260601330', cus: 'EmdeesFoods', lv: 'KTT', why: 'Nợ quá hạn > 90 ngày 45,0 tr · tổng nợ 805,0 / HM 900,0 tr', wait: 'Ghế [ACC-CA] trống · leo lên BOD-DIR PHILIP · đang chờ' },
  { k: 'Giao DO hàng nhập', job: 'SI260600980', cus: 'NAMMY', lv: 'Trưởng KD', why: 'Có nợ 61–90 ngày 95,0 tr · còn trong hạn mức (còn 95,0 tr)', wait: '[SALE-MGR] DAVID — chờ bạn' },
];

export function ArDock({ rows, dock, money, onOpen }: { rows: readonly ArRow[]; dock: ReturnType<typeof useArDock>; money: boolean; onOpen: (customer: string) => void }) {
  const t = useT();
  const [done, setDone] = useState<Readonly<Record<string, boolean>>>({});
  if (!dock.open) return null;
  const m = (x: string) => (money ? x : MASK);
  const sum = arSummary(rows);
  const ag = [sum.current, sum.b1, sum.b2, sum.b3, sum.b4];
  const C = 2 * Math.PI * 52;
  let acc = 0;
  const wk = RCP_ROWS.filter((r) => /^(1[6-9]|2[0-2])\/Oct\/2026$/.test(r.date));
  const now = wk.reduce((a, r) => a + r.amt, 0);
  const tr = [0.74, 0.88, 0.69, 1.02, 0.91, 1].map((f, i) => (i === 5 ? now : Math.round(now * f)));
  const labs = ['11/Sep', '18/Sep', '25/Sep', '02/Oct', '09/Oct', '16/Oct'];
  const mx = Math.max(...tr, 1);
  const mn = Math.min(...tr);
  const X = (i: number) => 16 + (i * 288) / 5;
  const Y = (v: number) => 100 - 6 - ((v - mn) / (mx - mn || 1)) * (100 - 6 - 22);
  const line = tr.map((v, i) => `${i ? 'L' : 'M'}${X(i).toFixed(1)} ${Y(v).toFixed(1)}`).join(' ');
  const prev = tr[4] ?? 0;
  const dp = prev ? ((now - prev) / prev) * 100 : 0;
  const top = rows.slice().sort((a, b) => b.over - a.over).filter((r) => r.over > 0).slice(0, 5);
  const topMax = Math.max(...top.map((r) => r.over), 1);
  const tabs = [{ key: 'an' as const, label: t('ad.tab.an') }, { key: 'task' as const, label: t('ad.tab.task'), n: 5 }, { key: 'appr' as const, label: t('ad.tab.appr'), n: 2 }];
  const mid = sum.total >= 1000 ? t('ad.bn', { n: nf(sum.total / 1000, 2) }) : t('ad.tr', { n: nf(sum.total) });

  return (
    <DockPanel tabs={tabs} tab={dock.tab} onTab={dock.show} onClose={dock.close}>
      {dock.tab === 'an' && (
        <>
          <section className={s.sec}>
            <div className={s.hd}><h2>{t('ad.sum')}</h2><span>{t('ad.custs', { n: rows.length })}</span></div>
            <div className={s.kpis}>
              <div><div>{t('ad.rec')}</div><b>{m(nf(sum.total))}</b> <span>tr</span></div>
              <div><div>{t('ad.over')}</div><b className={s.err}>{m(nf(sum.over))}</b> <span>{money && sum.total ? `${Math.round((sum.over / sum.total) * 100)}%` : ''}</span></div>
              <div><div>DSO</div><b>{money ? sum.dso : '•••'}</b> <span>{t('ad.days')}</span></div>
              <div><div>{t('ad.held')}</div><b className={s.warn}>{sum.held}</b> <span>{t('ad.custUnit')}</span></div>
            </div>
          </section>
          <section className={s.sec} aria-label={t('ad.aging')}>
            <h2 className={s.h2}>{t('ad.aging')}</h2>
            <div className={s.agRow}>
              <svg role="img" aria-label={t('ad.agingAria', { v: money ? AGK.map((k, i) => `${t(k)} ${t('ad.tr', { n: nf(ag[i] ?? 0) })}`).join(', ') : t('ad.hidden') })} width="112" height="112" viewBox="0 0 132 132">
                <circle cx="66" cy="66" r="52" fill="none" stroke="var(--line)" strokeWidth="16" />
                {ag.map((v, i) => {
                  const L = money && sum.total ? (Math.max(v, 0) / sum.total) * C : 0;
                  const g = L > 6 ? 3 : 0;
                  const el = <circle key={AGK[i]} cx="66" cy="66" r="52" fill="none" stroke={AGC[i]} strokeWidth="16" strokeDasharray={`${Math.max(L - g, 0).toFixed(1)} ${(C - L + g).toFixed(1)}`} strokeDashoffset={(-acc).toFixed(1)} transform="rotate(-90 66 66)" />;
                  acc += L;
                  return el;
                })}
                <text x="66" y="66" textAnchor="middle" fontSize="17" fontWeight="550" fill="var(--tx)">{m(mid)}</text>
                <text x="66" y="82" textAnchor="middle" fontSize="11" fill="var(--lb)">{t('ad.recLc')}</text>
              </svg>
              <div className={s.leg}>
                {AGK.map((k, i) => (
                  <div key={k}><i style={{ background: AGC[i] }} /><span>{t(k)}</span><b>{m(nf(ag[i] ?? 0))}</b><em>{money && sum.total ? `${Math.round(((ag[i] ?? 0) / sum.total) * 100)}%` : '—'}</em></div>
                ))}
              </div>
            </div>
          </section>
          <section className={s.sec} aria-label={t('ad.trend')}>
            <div className={s.hd}><h2>{t('ad.trendH')}</h2><span>{t('ad.perWeek')}</span></div>
            {money ? (
              <>
                <svg role="img" aria-label={t('ad.trendAria', { v: tr.map((v) => nf(v, 0)).join(', '), n: nf(now) })} width="100%" height="128" viewBox="0 0 320 128" style={{ display: 'block', marginTop: 6, overflow: 'visible' }}>
                  <line x1="16" y1="106" x2="304" y2="106" stroke="var(--bd)" />
                  <path d={`${line} L304 106 L16 106 Z`} fill="var(--d-blue)" opacity="0.08" />
                  <path d={line} fill="none" stroke="var(--d-blue)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  {tr.map((v, i) => (
                    <g key={labs[i]}>
                      <circle cx={X(i)} cy={Y(v)} r={i === 5 ? 5 : 4} fill="var(--card)" stroke="var(--d-blue)" strokeWidth="2.5" />
                      <text x={X(i)} y={Y(v) - 10} textAnchor="middle" fontSize="11" fontWeight="550" fill="var(--tx)">{nf(v, 0)}</text>
                      <text x={X(i)} y="124" textAnchor="middle" fontSize="11" fill="var(--lb)">{labs[i]}</text>
                    </g>
                  ))}
                </svg>
                <div className={s.trFoot}><span>{t('ad.thisWeek')} <b>{t('ad.tr', { n: nf(now) })}</b> · {t('ad.rcpN', { n: wk.length })}</span><span className={dp >= 0 ? s.ok : s.warn}>{dp >= 0 ? '+' : '−'}{t('ad.vsPrev', { n: nf(Math.abs(dp)) })}</span></div>
              </>
            ) : (
              <div className={s.mask}>••••••</div>
            )}
          </section>
          <section className={s.sec}>
            <div className={s.hd}><h2>{t('ad.topOver')}</h2><span>tr ₫</span></div>
            <div role="group" aria-label={t('ad.topAria', { v: money ? top.map((r) => `${r.customer} ${t('ad.tr', { n: nf(r.over) })}`).join(', ') : t('ad.hidden') })}>
              {top.map((r) => (
                <button key={r.customer} type="button" className={s.topRow} aria-label={t('ad.open', { c: r.customer, n: nf(r.over) })} onClick={() => onOpen(r.customer)}>
                  <span>{r.customer}</span>
                  <div className={s.track}><div style={{ width: money ? `${Math.max(2, Math.round((r.over / topMax) * 100))}%` : '0%', background: r.d61_90 + r.d90 > 0 ? 'var(--err-bd)' : 'var(--warn)' }} /></div>
                  <b>{m(nf(r.over))}</b>
                </button>
              ))}
            </div>
          </section>
        </>
      )}
      {dock.tab === 'task' &&
        TASKS.map(([g, tone, items], gi) => (
          <div key={g}>
            <div className={`${s.grp} ${s[tone]}`}><span>{t(g)}</span><span>{items.length}</span></div>
            {items.map(([code, k, due, who], ii) => {
              const id = `${gi}-${ii}`;
              return (
                <div key={id} className={s.task} style={{ opacity: done[id] ? 0.5 : 1 }}>
                  <input type="checkbox" aria-label={t('ad.done', { k })} checked={!!done[id]} onChange={() => setDone({ ...done, [id]: !done[id] })} />
                  <div>
                    <div className={s.tk} style={{ textDecoration: done[id] ? 'line-through' : 'none' }}><span className={s.code}>{code}</span>{k}</div>
                    <div className={s.due}><span className={gi === 1 ? s.err : ''}>{due}</span> · {who}</div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      {dock.tab === 'appr' &&
        APPRS.map((a) => (
          <section key={a.job} className={s.appr}>
            <div className={s.hd}><div><b>{a.k}</b><div className={s.sub}>{a.job} · {a.cus}</div></div><span className={s.lv}>{a.lv}</span></div>
            <div className={s.why}>{a.why}</div>
            <div className={s.sub}>{a.wait}</div>
          </section>
        ))}
    </DockPanel>
  );
}
